import {
  BRAND, DEFAULT_CONFIG, changedFromDefaults, checkConfig, describeRules, requiredSets, roundsAvailable,
  type MatchConfig, type PeekMode, type RuleLine, type WordListId,
} from '@nt2/engine';
import { clearHistory, gridText, readHistory } from '../state/history.js';
import { audio, THEMES, type ThemeId } from '../audio/engine.js';
import {
  COUNTDOWN_STYLES, countdownVoices, previewCountdown, type CountdownStyle,
} from '../audio/countdown.js';
import { applyDocumentPrefs, prefs, PREFS_CHANGED, resetHints, setPrefs } from '../state/prefs.js';
import { el, on } from './dom.js';
import { confirmModal, openModal } from './modal.js';
import type { PackMeta } from '../dict/pack.js';
import { CONTROLLER, contactConfigured, openContact } from './contact.js';
import { GRID_STRENGTHS, SCENES, sceneById, type SceneId } from './scenes.js';
import { versionDetail } from '../version.js';

// ---------------------------------------------------------------------------
// Small form builders
// ---------------------------------------------------------------------------

function row(label: string, control: Node, hint?: string): HTMLElement {
  return el('div', { class: 'setting' },
    el('div', { class: 'setting__label', text: label }),
    el('div', { class: 'setting__control' }, control),
    hint ? el('div', { class: 'setting__hint', text: hint }) : null);
}

function toggle(checked: boolean, onChange: (v: boolean) => void): HTMLElement {
  const input = el('input', { type: 'checkbox', checked });
  on(input, 'change', () => onChange(input.checked));
  return el('label', { class: 'switch' }, input, el('span', { class: 'track' }));
}

function picker<T extends string>(
  value: T, options: Array<{ value: T; label: string }>, onChange: (v: T) => void,
): HTMLSelectElement {
  const sel = el('select', { class: 'input' }) as HTMLSelectElement;
  for (const o of options) {
    sel.append(el('option', { value: o.value, text: o.label, selected: o.value === value }));
  }
  on(sel, 'change', () => onChange(sel.value as T));
  return sel;
}

/**
 * Pick a background scene by looking at it.
 *
 * A list of names — Halo, Library, Dusk — tells nobody anything, and the whole
 * point of offering scenes is taste. So each option is painted with the scene
 * it selects, it applies the moment it is tapped, and the dialog it is in
 * floats over the thing being changed: flick through and watch the page behind
 * the panel change. No preview, no apply, no guessing.
 */
function scenePicker(
  current: SceneId, where: 'menu' | 'board', onPick: (id: SceneId) => void,
): HTMLElement {
  const box = el('div', { class: 'scenes', role: 'radiogroup' });
  const note = el('div', { class: 'setting__hint', style: 'margin-top:6px' });
  const paint = (id: SceneId): void => {
    const sc = sceneById(id);
    note.textContent = sc.note + (sc.restful ? '' : ' Best kept to the menus.');
  };
  // Rooms first on the menu picker and surfaces first on the board one: they
  // are what each place is most likely to want.
  const fits = SCENES.filter((sc) => sc.for === where || sc.for === 'both')
    .sort((a, b) => Number(b.for === where) - Number(a.for === where));
  for (const sc of fits) {
    const swatch = el('div', { class: 'scene__swatch' });
    const btn = el('button', {
      class: `scene scene--${sc.id}`, type: 'button', role: 'radio',
      'aria-checked': String(sc.id === current), title: sc.note,
    }, swatch, el('span', { class: 'scene__name', text: sc.name }),
       sc.restful ? null : el('span', {
         class: 'scene__busy', title: 'Busier — better on a menu than behind a board',
       }));
    on(btn, 'click', () => {
      for (const other of box.querySelectorAll('.scene')) {
        other.setAttribute('aria-checked', String(other === btn));
      }
      paint(sc.id);
      onPick(sc.id);
    });
    box.append(btn);
  }
  paint(current);
  return el('div', {}, box, note);
}

function slider(
  value: number, min: number, max: number, step: number, onChange: (v: number) => void,
): HTMLElement {
  const out = el('output', { text: String(value), style: 'font-family:var(--font-num);min-width:2ch' });
  const input = el('input', {
    type: 'range', min, max, step, value, class: 'input',
    style: 'padding:0;background:transparent;border:0',
  }) as HTMLInputElement;
  on(input, 'input', () => { out.textContent = input.value; onChange(Number(input.value)); });
  return el('div', { style: 'display:flex;gap:10px;align-items:center' }, input, out);
}

// ---------------------------------------------------------------------------
// Device settings
// ---------------------------------------------------------------------------

/**
 * What the Settings page needs from the rest of the app to show and change
 * the rules this device uses for new tables. Registered by main.ts, which owns
 * the remembered rules and the word lists.
 */
export interface RulesHooks {
  current(): MatchConfig;
  edit(focus?: string): void;
  reset(): void;
}
let rulesHooks: RulesHooks | null = null;
export function setRulesHooks(h: RulesHooks): void { rulesHooks = h; }

type SettingsTab = 'look' | 'playing' | 'sound' | 'rules' | 'history' | 'about';

/**
 * Settings: one page, from the front door or from a game.
 *
 * Everything that belongs to this device lives here — how it looks, how
 * letters are placed, sound, hints — together with the rules it uses for new
 * tables and the history of finished games. It used to be reachable only from
 * inside a match, so the backdrop behind the menus could only be changed from
 * a screen that did not show it.
 */
export function openSettings(start: SettingsTab = 'look'): void {
  const p = prefs();
  const tabs: Array<{ id: SettingsTab; label: string }> = [
    { id: 'look', label: 'Look' }, { id: 'playing', label: 'Playing' },
    { id: 'sound', label: 'Sound' }, { id: 'rules', label: 'Rules' },
    { id: 'history', label: 'History' }, { id: 'about', label: 'About' },
  ];
  const panes = new Map<SettingsTab, HTMLElement>(
    tabs.map((t) => [t.id, el('div', { class: 'settings__pane', 'data-tab': t.id })]));
  let body = panes.get('look')!;
  const section = (tab: SettingsTab, title: string): void => {
    body = panes.get(tab)!;
    body.append(el('h3', { text: title, style: head() }));
  };

  section('look', 'Look');
  body.append(row('Theme',
    picker(p.theme, [
      { value: 'dark', label: 'Dark' },
      { value: 'light', label: 'Light' },
      { value: 'system', label: 'Match my device' },
    ], (v) => { setPrefs({ theme: v }); applyDocumentPrefs(); }),
    'Dark is the design; light is a proper second theme rather than an inversion.'));

  body.append(el('div', { class: 'setting__label', style: 'margin:14px 0 2px', text: 'Behind the menus' }));
  body.append(scenePicker(p.sceneMenu, 'menu', (v) => setPrefs({ sceneMenu: v })));
  body.append(el('div', { class: 'setting__label', style: 'margin:16px 0 2px', text: 'Behind the board' }));
  body.append(scenePicker(p.sceneGame, 'board', (v) => setPrefs({ sceneGame: v })));
  body.append(row('Grid lines on the board',
    picker(p.grid, GRID_STRENGTHS.map((g) => ({ value: g.id, label: g.name })),
      (v) => setPrefs({ grid: v })),
    'How clearly the squares are drawn. Clear helps line words up; none shows off the surface.'));
  body.append(el('div', {
    class: 'setting__hint', style: 'margin-top:6px',
    text: 'Two choices, because they are looked at differently: a menu for ten ' +
          'seconds, a board for twenty minutes with a hundred letters on it. ' +
          'Every scene works in both themes.',
  }));
  section('look', 'Readability');
  body.append(row('Higher contrast',
    toggle(p.contrast === 'high', (v) => setPrefs({ contrast: v ? 'high' : 'normal' })),
    'Swaps the valid/invalid colours for a pair that stays distinct with red-green colour blindness.'));
  body.append(row('Tile size',
    picker(String(p.tileSize) as string, [
      { value: '0', label: 'Automatic' }, { value: '32', label: 'Small' },
      { value: '40', label: 'Medium' }, { value: '52', label: 'Large' },
      { value: '64', label: 'Extra large' },
    ], (v) => setPrefs({ tileSize: Number(v) })),
    'Bigger tiles mean fewer on screen, but easier targets.'));
  body.append(row('Flag bad words on the grid',
    toggle(p.showRunTags, (v) => setPrefs({ showRunTags: v })),
    'Only has an effect when the match allows live word checking.'));

  section('playing', 'Placing letters');
  body.append(row('Tapped letters go',
    picker(p.typeDir, [
      { value: 'across', label: 'Across →' },
      { value: 'down', label: 'Down ↓' },
    ], (v) => setPrefs({ typeDir: v })),
    'Tap a letter in your rack and it goes in the next free square this way ' +
    'from the cursor. Tapping the cursor square, or the arrow button on the ' +
    'board, switches it during a game.'));
  body.append(row('Guess the direction',
    toggle(p.autoDir, (v) => setPrefs({ autoDir: v })),
    'Tap the square under a letter and letters go down; the square after one, ' +
    'across. Tap a letter on the board to build through it: the cursor jumps ' +
    'just past it, going the way it is free. Tap it again for the other way.'));
  body.append(row('Drag reach',
    picker(String(p.dragReach), [
      { value: '1', label: 'Off' },
      { value: '0.75', label: '¾ of the way' },
      { value: '0.5', label: 'Half-way' },
      { value: '0.33', label: 'A third' },
    ], (v) => setPrefs({ dragReach: Number(v) })),
    'How far you have to drag a letter up from the rack. At half-way, moving it ' +
    'half the distance is enough: it aims twice as far, and a green square ' +
    'shows where it will land when you let go.'));
  body.append(row('Swap guard',
    picker(String(p.swapGuard), [
      { value: '0.5', label: '50%' },
      { value: '0.75', label: '75%' },
      { value: '0.9', label: '90%' },
      { value: '1', label: 'Never swap' },
    ], (v) => setPrefs({ swapGuard: Number(v) })),
    'Drop a letter on another and they swap places — but only if you let go ' +
    'well inside it: this far in from its edge. Anywhere else, the letter goes ' +
    'to the free square beside it, which is nearly always what was meant.'));

  section('sound', 'Sound');
  body.append(row('Mute everything',
    toggle(p.muted, (v) => { setPrefs({ muted: v }); audio.configure({ muted: v }); })));
  const themeSel = picker(p.soundTheme, THEMES.map((t) => ({ value: t.id, label: t.name })),
    (v: ThemeId) => {
      setPrefs({ soundTheme: v });
      audio.configure({ theme: v, muted: false });
      audio.play('word');
      desc.textContent = THEMES.find((t) => t.id === v)?.description ?? '';
    });
  const desc = el('div', {
    class: 'setting__hint',
    text: THEMES.find((t) => t.id === p.soundTheme)?.description ?? '',
  });
  body.append(row('Atmosphere', themeSel));
  body.append(desc);
  body.append(row('Volume',
    slider(Math.round(p.volume * 100), 0, 100, 5, (v) => {
      setPrefs({ volume: v / 100 });
      audio.configure({ volume: v / 100 });
    })));
  section('sound', 'Countdown before a match');
  body.append(countdownChooser());

  section('playing', 'Guidance');
  body.append(row('Show hints',
    toggle(p.hintsEnabled, (v) => setPrefs({ hintsEnabled: v })),
    'Short tips along the bottom of the board. They never block anything and never need dismissing.'));
  const resetBtn = el('button', { class: 'btn btn--sm', type: 'button', text: 'Show all hints again' });
  on(resetBtn, 'click', () => {
    resetHints();
    resetBtn.textContent = 'Hints reset ✓';
    setTimeout(() => { resetBtn.textContent = 'Show all hints again'; }, 1600);
  });
  body.append(row('Dismissed hints', resetBtn,
    `${p.hintsSeen.length} hint${p.hintsSeen.length === 1 ? '' : 's'} silenced on this device.`));

  body = panes.get('about')!;
  if (contactConfigured()) {
    section('about', 'Anything wrong?');
    const write = el('button', {
      class: 'btn btn--sm', type: 'button', text: 'Report a problem',
    });
    on(write, 'click', () => openContact('Feedback'));
    body.append(row('Tell us about it', write,
      'Goes straight to whoever runs the game. An email address is only ' +
      'needed if you want a reply.'));
  }

  // Which build this is. A bug report without it cannot be placed in the
  // history, and nobody can read a version number that is not on the screen.
  section('about', 'Version');
  body.append(el('div', {
    class: 'setting__hint',
    style: 'font-family:var(--font-num);-webkit-user-select:text;user-select:text',
    text: versionDetail(),
  }));
  body.append(el('div', {
    class: 'setting__hint',
    text: 'The app checks for a newer version whenever you come back to it, and ' +
          'updates itself from the front page.',
  }));

  rulesPane(panes.get('rules')!);
  historyPane(panes.get('history')!);

  const bar = el('div', { class: 'settings__tabs', role: 'tablist' });
  const show = (id: SettingsTab): void => {
    for (const [tab, pane] of panes) pane.hidden = tab !== id;
    for (const b of bar.children) (b as HTMLElement).setAttribute('aria-selected', String((b as HTMLElement).dataset['tab'] === id));
    panes.get(id)!.closest('.modal__body')?.scrollTo({ top: 0 });
  };
  for (const t of tabs) {
    const b = el('button', { class: 'settings__tab', type: 'button', role: 'tab', 'data-tab': t.id, text: t.label });
    on(b, 'click', () => show(t.id));
    bar.append(b);
  }
  const page = el('div', { class: 'settings' }, bar, ...panes.values());
  openModal({ title: 'Settings', body: page, page: true,
    actions: [{ label: 'Done', kind: 'primary', onClick: () => true }] });
  show(start);
}

/**
 * Choose what the start countdown sounds like, by hearing it.
 *
 * Tapping a style plays the whole countdown and makes it this device's
 * choice; the spoken ones use the voice picked underneath, from whatever
 * voices this phone or computer has.
 */
function countdownChooser(): HTMLElement {
  const box = el('div', { class: 'cdchooser' });
  let stop: (() => void) | null = null;
  const play = (style: CountdownStyle): void => {
    stop?.();
    stop = previewCountdown(style, prefs().countdownVoice);
  };
  box.append(el('div', { class: 'setting__hint', style: 'margin-bottom:8px',
    text: 'Plays at a table of two or more when the host starts, so everyone begins ' +
          'together. Tap one to hear it and use it.' }));
  const list = el('div', { class: 'cdchooser__list', role: 'radiogroup' });
  const items: HTMLElement[] = [];
  for (const s of COUNTDOWN_STYLES) {
    const item = el('button', {
      class: 'cdchooser__item', type: 'button', role: 'radio', 'data-style': s.id,
      'aria-checked': String(prefs().countdownSound === s.id),
    }, el('span', { class: 'cdchooser__play', 'aria-hidden': 'true', text: '▶' }),
       el('span', { class: 'cdchooser__text' },
         el('b', { text: s.name }), el('span', { text: s.note })));
    on(item, 'click', () => {
      setPrefs({ countdownSound: s.id });
      for (const i of items) i.setAttribute('aria-checked', String(i === item));
      play(s.id);
    });
    items.push(item);
    list.append(item);
  }
  box.append(list);

  // The voice for the spoken styles. The list arrives late on some browsers.
  const voiceSel = el('select', { class: 'input' }) as HTMLSelectElement;
  const fill = (): void => {
    const voices = countdownVoices();
    voiceSel.replaceChildren(el('option', { value: '', text: 'Usual voice for this device' }),
      ...voices.map((v) => el('option', {
        value: v.voiceURI, text: `${v.name} (${v.lang})`, selected: v.voiceURI === prefs().countdownVoice,
      })));
  };
  fill();
  try { speechSynthesis.addEventListener('voiceschanged', fill); } catch { /* no speech */ }
  on(voiceSel, 'change', () => {
    setPrefs({ countdownVoice: voiceSel.value });
    const style = prefs().countdownSound;
    play(COUNTDOWN_STYLES.find((x) => x.id === style)?.spoken ? style : 'voice');
  });
  box.append(row('Voice for the read-out styles', voiceSel,
    'Which voices there are depends on the phone or computer. Changing it plays a sample.'));
  return box;
}

/** The rules this device uses for tables it starts, with every value tappable. */
function rulesPane(box: HTMLElement): void {
  box.append(el('h3', { text: 'Rules for new tables', style: head() }));
  if (!rulesHooks) {
    box.append(el('div', { class: 'setting__hint', text: 'Available from the front page.' }));
    return;
  }
  const hooks = rulesHooks;
  const draw = (): void => {
    box.querySelector('.settings__rules')?.remove();
    const cfg = hooks.current();
    const changed = changedFromDefaults(cfg);
    const inner = el('div', { class: 'settings__rules' });
    inner.append(el('div', {
      class: 'setting__hint',
      text: (changed.length ? `${changed.length} change${changed.length === 1 ? '' : 's'} from the standard game. `
        : 'The standard game. ') +
        'These are used whenever you start a table, solo or with others, and ' +
        'can still be changed in the waiting room. Tap a yellow value to change it.',
    }));
    inner.append(el('div', { class: 'rules' },
      ...describeRules(cfg, 2).map((l) => ruleLine(l, (setting) => hooks.edit(setting)))));
    const edit = el('button', { class: 'btn btn--sm btn--primary', type: 'button', text: 'Change the rules' });
    on(edit, 'click', () => hooks.edit());
    const reset = el('button', { class: 'btn btn--sm', type: 'button', text: 'Back to the standard rules' });
    reset.hidden = changed.length === 0;
    on(reset, 'click', () => { hooks.reset(); draw(); });
    inner.append(el('div', { class: 'lobby__row', style: 'margin-top:10px' }, edit, reset));
    box.append(inner);
  };
  draw();
  // Redrawn when the editor applies, which sets a preference.
  const redraw = (): void => { if (box.isConnected) draw(); else window.removeEventListener(PREFS_CHANGED, redraw); };
  window.addEventListener(PREFS_CHANGED, redraw);
}

/**
 * Finished games kept on this device, each one exportable as JSON.
 *
 * The export is the record itself — rules, every grid square by square, and
 * how each score was made — so it can be kept, compared or shared with
 * anybody, with or without the game.
 */
function historyPane(box: HTMLElement): void {
  const games = readHistory();
  box.append(el('h3', { text: 'Game history', style: head() }));
  if (games.length === 0) {
    box.append(el('div', {
      class: 'setting__hint',
      text: 'Every game you finish is kept here, on this device only: the grids, ' +
            'the scores and how they were made. None yet.',
    }));
    return;
  }
  const stamp = new Date().toISOString().slice(0, 10);
  const all = el('button', { class: 'btn btn--sm btn--primary', type: 'button', text: 'Export all' });
  on(all, 'click', () => exportJson(`puletiwo-history-${stamp}.json`, games));
  const wipe = el('button', { class: 'btn btn--sm btn--ghost', type: 'button', text: 'Delete history' });
  on(wipe, 'click', async () => {
    if (!await confirmModal('Delete the game history?',
      `All ${games.length} finished game${games.length === 1 ? '' : 's'} kept on this device will be gone. Export first if you want to keep them.`,
      'Delete')) return;
    clearHistory();
    box.replaceChildren();
    historyPane(box);
  });
  box.append(el('div', { class: 'setting__hint',
    text: `${games.length} game${games.length === 1 ? '' : 's'} kept on this device. ` +
          'Export saves them as a JSON file you can keep or send.' }));
  box.append(el('div', { class: 'lobby__row', style: 'margin:8px 0 12px' }, all, wipe));

  for (const g of games) {
    const me = g.players.find((x) => x.you);
    const place = me ? g.players.findIndex((x) => x === me) + 1 : 0;
    const when = new Date(g.finishedAt);
    const title = `${when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ` +
      `${when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} · ` +
      (g.kind === 'solo' ? 'Solo' : `Table ${g.table}`) +
      (me ? ` · ${me.score} pts` + (g.players.length > 1 ? `, ${ordinal(place)} of ${g.players.length}` : '') : '');
    const item = el('details', { class: 'history__item' }, el('summary', { text: title }));
    const detail = el('div', { class: 'history__detail' });
    for (const pl of g.players) {
      detail.append(el('div', { class: 'series__row' + (pl.you ? ' series__row--you' : '') },
        el('span', { class: 'series__name', text: pl.name + (pl.you ? ' (you)' : '') + (pl.won ? ' — won' : '') }),
        el('span', { class: 'series__wins', text: pl.wentOut ? 'went out' : `${pl.lettersInHand} in hand` }),
        el('span', { class: 'series__pts', text: String(pl.score) })));
      const grid = gridText(pl.grid);
      if (grid) detail.append(el('pre', { class: 'history__grid', text: grid }));
    }
    const file = `puletiwo-${when.toISOString().slice(0, 16).replace(/[:T]/g, '-')}.json`;
    const copy = el('button', { class: 'btn btn--sm', type: 'button', text: 'Copy JSON' });
    on(copy, 'click', async () => {
      try { await navigator.clipboard.writeText(JSON.stringify(g, null, 2)); copy.textContent = 'Copied ✓'; }
      catch { copy.textContent = 'Could not copy'; }
    });
    const share = el('button', { class: 'btn btn--sm', type: 'button', text: 'Share' });
    on(share, 'click', () => void shareJson(file, g));
    const save = el('button', { class: 'btn btn--sm btn--ghost', type: 'button', text: 'Download' });
    on(save, 'click', () => exportJson(file, g));
    detail.append(el('div', { class: 'lobby__row', style: 'margin-top:8px' }, share, copy, save));
    item.append(detail);
    box.append(item);
  }
}

const ordinal = (n: number): string =>
  `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;

function exportJson(name: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = el('a', { href: url, download: name }) as HTMLAnchorElement;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** The phone's own share sheet with the file attached, where it can; otherwise a download. */
async function shareJson(name: string, data: unknown): Promise<void> {
  const text = JSON.stringify(data, null, 2);
  const file = new File([text], name, { type: 'application/json' });
  const nav = navigator as Navigator & { canShare?(d: { files: File[] }): boolean };
  if (nav.canShare?.({ files: [file] })) {
    try { await navigator.share({ files: [file], title: name }); return; }
    catch { return; /* dismissed */ }
  }
  exportJson(name, data);
}

const head = (): string =>
  'margin:18px 0 6px;font-size:12px;text-transform:uppercase;letter-spacing:.07em;color:var(--text-2)';

// ---------------------------------------------------------------------------
// Match options (host, lobby only)
// ---------------------------------------------------------------------------

export function openMatchOptions(
  config: MatchConfig,
  seatCount: number,
  packs: PackMeta[],
  onApply: (patch: Partial<MatchConfig>) => void,
  opts: {
    /** A setting to bring into view: the name shown against a value in the rules. */
    focus?: string;
    title?: string;
  } = {},
): void {
  let draft: MatchConfig = { ...config, peek: { ...config.peek }, visibility: { ...config.visibility } };
  const body = el('div');
  const warn = el('div', { class: 'setting__hint', style: 'color:var(--warn)' });

  const refresh = (): void => {
    const problem = checkConfig(draft, Math.max(1, seatCount));
    const sets = requiredSets(Math.max(1, seatCount), draft);
    const can = roundsAvailable(Math.max(1, seatCount), draft.startingTiles, draft.drawPerRound, sets);
    warn.textContent = problem
      ?? `${seatCount || 1} seat${seatCount === 1 ? '' : 's'} · ${sets} tile set${sets === 1 ? '' : 's'} ` +
         `· ${sets * 100} tiles minted, ${Math.max(0, sets * 100 - seatCount * (draft.startingTiles + draft.rounds * draft.drawPerRound))} left over ` +
         `· up to ${can} rounds.`;
    warn.style.color = problem ? 'var(--bad)' : 'var(--text-2)';
  };

  body.append(el('h3', { text: 'Shape of the match', style: head() }));
  body.append(row('Starting letters',
    slider(draft.startingTiles, 2, 21, 1, (v) => { draft.startingTiles = v; refresh(); }),
    'Seven is the classic. Fewer makes a faster, twitchier game; more suits one long puzzle.'));
  body.append(row('Draw rounds',
    slider(draft.rounds, 1, 20, 1, (v) => { draft.rounds = v; refresh(); }),
    'How many times everyone draws before the bag runs dry. The bag is sized so this is the same for 2 players or 20.'));
  body.append(row('Letters per round',
    slider(draft.drawPerRound, 1, 5, 1, (v) => { draft.drawPerRound = v; refresh(); }),
    'This is also the number in the call: at 3 the table shouts "TAKE 3".'));
  body.append(row('Force a draw every',
    picker(String(draft.forceDrawMs), [
      { value: '0', label: 'Never' }, { value: '120000', label: '2 minutes' },
      { value: '180000', label: '3 minutes' }, { value: '300000', label: '5 minutes' },
      { value: '600000', label: '10 minutes' },
    ], (v) => { draft.forceDrawMs = Number(v); }),
    'Everyone draws automatically if nobody has called for this long. Keeps a ' +
    'table moving when one player is stuck on a hard rack. Any draw restarts the clock.'));
  body.append(row('Time limit',
    picker(String(draft.timeLimitMs), [
      { value: '0', label: 'No limit' }, { value: '300000', label: '5 minutes' },
      { value: '600000', label: '10 minutes' }, { value: '1200000', label: '20 minutes' },
      { value: '2400000', label: '40 minutes' },
    ], (v) => { draft.timeLimitMs = Number(v); })));
  body.append(warn);

  body.append(el('h3', { text: 'Words', style: head() }));
  body.append(row('Word list',
    picker(draft.wordList, [
      ...packs.map((m) => ({ value: m.id as WordListId, label: `${m.name} (${fmt(m.count)} words)` })),
      { value: 'custom' as WordListId, label: 'Everything (all lists combined)' },
    ], (v) => { draft.wordList = v; showPack(); }),
    ''));
  const packNote = el('div', { class: 'setting__hint' });
  const showPack = (): void => {
    const m = packs.find((x) => x.id === draft.wordList);
    packNote.textContent = m
      ? `${m.name} — ${fmt(m.count)} words. ${m.licence}. ${m.attribution}`
      : 'Every bundled list at once — the most permissive setting.';
  };
  showPack();
  body.append(packNote);
  body.append(row('Spelling accepted',
    picker(draft.spelling, [
      { value: 'both' as const, label: 'British and American' },
      { value: 'british' as const, label: 'British only (colour, centre)' },
      { value: 'american' as const, label: 'American only (color, center)' },
    ], (v) => { draft.spelling = v; }),
    'Swaps the spelling of words the list already has — favourite or favorite, ' +
    'travelled or traveled — and never adds new words. British accepts -ize as ' +
    'well as -ise, as British English does. Spelling pairs from SCOWL ' +
    '(wordlist.aspell.net), © 2000-2026 Kevin Atkinson.'));
  body.append(row('Shortest word allowed',
    picker(String(draft.minWordLength), [
      { value: '2', label: '2 letters (standard)' },
      { value: '3', label: '3 letters' },
      { value: '4', label: '4 letters' },
    ], (v) => { draft.minWordLength = Number(v); }),
    'Banning two-letter words takes away the obscure ones experienced players lean on.'));

  body.append(el('h3', { text: 'Scoring', style: head() }));
  body.append(el('div', {
    class: 'setting__hint', style: 'margin:0 0 8px',
    text: 'Every letter is worth its face value once for each word it is in, ' +
          'so a letter where two words cross is paid twice. Letters you never ' +
          'used — in your hand, or sitting on the board in nothing — are taken ' +
          'off again.',
  }));
  body.append(row('Countdown before the start',
    picker(String(draft.countdownSec), [
      { value: '5', label: '5 seconds' }, { value: '3', label: '3 seconds' }, { value: '0', label: 'None' },
    ], (v) => { draft.countdownSec = Number(v); refresh(); }),
    'At a table of two or more: once the host presses start, everyone counts ' +
    'down together and nobody can play until it reaches GO. Each player picks ' +
    'what it sounds like in Settings → Sound.'));
  body.append(row('Bonus for going out',
    slider(draft.goingOutBonus, 0, 50, 1, (v) => { draft.goingOutBonus = v; refresh(); }),
    'Whoever uses every letter ends the match and takes this. At 10 that is ' +
    'usually enough to win it; at 0 a denser grid can still take the match ' +
    'off them.'));
  body.append(row('Show each player their score',
    toggle(draft.showLiveScore, (v) => { draft.showLiveScore = v; }),
    'A running total in the top bar. It counts the words you have built ' +
    'without saying whether the list accepts them, so it is never a free ' +
    'word check.'));

  body.append(el('h3', { text: 'Checking words', style: head() }));
  body.append(row('When can players check?',
    picker(draft.peek.mode, [
      { value: 'limited' as PeekMode, label: 'A few checks each (recommended)' },
      { value: 'live' as PeekMode, label: 'Always — colour words live' },
      { value: 'off' as PeekMode, label: 'Never — commit and find out' },
    ], (v) => { draft.peek.mode = v; }),
    'Limiting checks is what stops the game becoming "type letters until something goes green".'));
  body.append(row('Checks to start with',
    slider(draft.peek.budget, 0, 10, 1, (v) => { draft.peek.budget = v; })));
  body.append(row('Extra checks per round',
    slider(draft.peek.budgetPerRound, 0, 3, 1, (v) => { draft.peek.budgetPerRound = v; })));
  body.append(row('Check window',
    picker(String(draft.peek.windowMs), [
      { value: '0', label: 'Any time' }, { value: '10000', label: '10 seconds' },
      { value: '20000', label: '20 seconds' }, { value: '45000', label: '45 seconds' },
    ], (v) => { draft.peek.windowMs = Number(v); }),
    'A check is only offered shortly after you move a tile, so it confirms a hunch rather than fishing.'));
  body.append(row('Show meanings with a check',
    toggle(draft.peek.showDefinition, (v) => { draft.peek.showDefinition = v; }),
    'Fetches a short definition where one is available. Validity never depends on it, so this works offline too.'));
  body.append(row('Calling too early costs',
    picker(draft.peek.penalty, [
      { value: 'cooldown' as const, label: 'A lockout' },
      { value: 'drawTiles' as const, label: 'Extra letters' },
      { value: 'none' as const, label: 'Nothing' },
    ], (v) => { draft.peek.penalty = v; })));

  body.append(el('h3', { text: 'What others can see', style: head() }));
  body.append(row('Looking at other players’ grids during the match',
    picker(draft.visibility.grids, [
      { value: 'never' as const, label: 'Not until the match ends' },
      { value: 'onRequest' as const, label: 'Any time — tap their name' },
      { value: 'afterFinish' as const, label: 'Once that player has finished' },
      { value: 'always' as const, label: 'Any time, sent to everyone' },
    ], (v) => { draft.visibility.grids = v; }),
    'After the match every grid can be seen: tap a name along the foot of the ' +
    'board. This is only about peeking while everyone is still racing.'));
  body.append(row('Letters left in hand',
    toggle(draft.visibility.rackCounts, (v) => { draft.visibility.rackCounts = v; }),
    'Just the count — the tension of seeing someone down to two tiles.'));
  body.append(row('The actual letters they hold',
    toggle(draft.visibility.rackLetters, (v) => { draft.visibility.rackLetters = v; }),
    'Fully open hands. Good for teaching a child, useless for a serious race.'));

  body.append(el('h3', { text: 'Trading letters in', style: head() }));
  body.append(row('Allow trading letters in',
    toggle(draft.dumpEnabled, (v) => { draft.dumpEnabled = v; }),
    'Hold a letter in your rack to put it back in the bag and draw more. Off ' +
    'unless you turn it on: the extra letters make it easier to score by ' +
    'spreading words out, rather than by making each letter count twice — ' +
    'across and down — which is the harder and better game.'));
  body.append(row('Letters received per trade',
    slider(draft.dumpRatio, 1, 5, 1, (v) => { draft.dumpRatio = v; })));

  refresh();
  openModal({
    title: opts.title ?? 'Match settings',
    body,
    actions: [
      { label: 'Reset to defaults', kind: 'ghost', onClick: () => {
        draft = { ...DEFAULT_CONFIG, seed: draft.seed };
        onApply(draft);
        return true;
      } },
      { label: 'Apply', kind: 'primary', onClick: () => {
        if (checkConfig(draft, Math.max(1, seatCount))) { refresh(); return false; }
        onApply(draft);
        return true;
      } },
    ],
  });
  if (opts.focus) focusSetting(body, opts.focus);
}

/**
 * Bring one setting into view and draw the eye to it.
 *
 * Tapping a highlighted value in the rules opens the options here: the value
 * said what the rule is, and this is where it changes, so the two should be
 * one tap apart rather than a hunt through a long dialog.
 */
function focusSetting(body: HTMLElement, name: string): void {
  const norm = (t: string): string => t.replace(/[’']/g, "'").trim().toLowerCase();
  const label = [...body.querySelectorAll<HTMLElement>('.setting__label')]
    .find((l) => norm(l.textContent ?? '') === norm(name));
  const rowEl = label?.closest<HTMLElement>('.setting');
  if (!rowEl) return;
  requestAnimationFrame(() => {
    rowEl.scrollIntoView({ block: 'center' });
    rowEl.classList.add('setting--focus');
    rowEl.querySelector<HTMLElement>('select, input, button')?.focus({ preventScroll: true });
    setTimeout(() => rowEl.classList.remove('setting--focus'), 2400);
  });
}

const fmt = (n: number): string => n.toLocaleString('en');

// ---------------------------------------------------------------------------
// How to play
// ---------------------------------------------------------------------------

/**
 * The written rules, as a block to put on a page.
 *
 * Shared by the how-to-play page, which puts the demonstration above it; the
 * rules of *this* match, with the settings the host chose.
 */
export function rulesBody(config?: MatchConfig): HTMLElement {
  const c = config ?? DEFAULT_CONFIG;
  const body = el('div', { style: 'color:var(--text-1);line-height:1.6' });
  const h = (t: string): HTMLElement => el('h3', { text: t, style: head() });
  const p = (t: string): HTMLElement => el('p', { text: t, style: 'margin:6px 0' });
  const ul = (...items: string[]): HTMLElement =>
    el('ul', { style: 'margin:6px 0;padding-left:20px' },
      ...items.map((i) => el('li', { text: i, style: 'margin:3px 0' })));

  body.append(h('The idea'));
  body.append(p(
    `Everyone starts at the same moment with ${c.startingTiles} letters and builds their own ` +
    'private crossword. There are no turns — it is a race.'));

  body.append(h('Building words'));
  body.append(ul(
    'Words read left-to-right across, and top-to-bottom down. Never backwards or diagonally.',
    'All your letters must join into one connected block, horizontally or vertically. Two separate groups are not allowed; diagonal contact does not connect them.',
    'Words share letters where they cross, like a crossword.',
    'Letters that touch in a line are read as one word. CAR beside MUCH is "carmuch", which is not a word. Rearrange or cross words so every line is valid and the whole grid stays connected.',
    `Every word must be at least ${c.minWordLength} letters long and in the match's word list.`,
  ));

  body.append(h(`Calling ${BRAND.callPhraseWords(c.drawPerRound)}`));
  body.append(p(
    'The moment your rack is empty and every word on your grid is valid, call ' +
    `${BRAND.callPhrase(c.drawPerRound)}. Everyone — you included — then draws ` +
    `${c.drawPerRound} more letter${c.drawPerRound === 1 ? '' : 's'} and carries straight on.`));
  body.append(p(
    'The number in the call is simply how many letters everyone draws, so it ' +
    'changes with the settings: a table drawing one shouts "TAKE 1".'));
  body.append(p(
    'Call it when your grid is not actually valid and you pay for it, so it is worth being sure.'));

  body.append(h('When everybody is stuck'));
  body.append(p(
    'Sometimes a rack simply cannot be played. Say you are stuck, and if every ' +
    'player still in the match says the same, the whole table draws and carries on. ' +
    'It costs a round from the bag like any other draw, so it is a rescue, not a tactic.'));
  if (c.forceDrawMs > 0) {
    body.append(p(
      `This match also draws automatically if nobody has called for ` +
      `${Math.round(c.forceDrawMs / 60000)} minutes.`));
  }

  body.append(h('Winning'));
  body.append(p(
    'The bag holds exactly enough letters for a fixed number of rounds, shared equally between ' +
    'seats. When there are not enough left to go round, the next player to empty their rack with ' +
    'a valid grid ends the match there and then.'));
  body.append(p(
    c.goingOutBonus > 0
      ? `Going out is worth ${c.goingOutBonus} points on top of your grid, which is usually ` +
        'enough to take the match — but the scores are what decide it, so a bigger grid can ' +
        'still come out ahead.'
      : 'This match gives no bonus for going out, so the highest score takes it however the ' +
        'match ends.'));

  body.append(h('Scoring'));
  body.append(ul(
    'Every letter is worth its face value — the small number in the corner of the tile.',
    'A letter counts once for each word it is part of, so a letter where two words cross is ' +
      'paid twice: once for the word across, once for the word down.',
    'Letters you never used are taken off again: the ones still in your hand, and any left ' +
      'sitting on the board in no word at all.',
    'A word the list rejects earns nothing, and its letters count as unused.',
    c.goingOutBonus > 0
      ? `Whoever uses every letter and ends the match adds ${c.goingOutBonus}.`
      : 'This match gives no bonus for going out.',
  ));
  if (c.showLiveScore) {
    body.append(p(
      'Your score so far is in the bar at the top. It counts up what you have built without ' +
      'telling you whether the word list accepts it, so it is never a free word check.'));
  }

  body.append(h('Checking a word'));
  body.append(p(
    c.peek.mode === 'live'
      ? 'This match colours your words as you go: green is in the list, red is not.'
      : c.peek.mode === 'off'
        ? 'This match gives no feedback at all. You find out when you call.'
        : `You get ${c.peek.budget} checks, plus ${c.peek.budgetPerRound} more each round. ` +
          'A check tells you which of your words are real' +
          (c.peek.showDefinition ? ', and what they mean.' : '.') +
          (c.peek.windowMs > 0
            ? ` Checks are only offered within ${Math.round(c.peek.windowMs / 1000)} seconds of moving a tile.`
            : '')));

  body.append(h('Watching the others'));
  body.append(p(
    c.visibility.grids === 'always'
      ? 'Everyone\u2019s grid is on show. Open the players panel and tap a name.'
      : c.visibility.grids === 'onRequest'
        ? 'Open the players panel and tap a name to watch that grid being built. ' +
          'Nobody\u2019s grid is sent to anybody until they go and look at it.'
        : c.visibility.grids === 'afterFinish'
          ? 'You can look at a grid once its player has finished, not before.'
          : 'This match keeps every grid private until it ends.'));

  body.append(h('Playing as a team'));
  body.append(p(
    'Two or more people can share one seat: one rack, one grid, two sets of eyes. The bag is ' +
    'divided by seat, not by person, so a team gets exactly the same letters as a solo player. ' +
    'It is the easiest way to bring a child into a game of adults.'));

  body.append(h('Controls'));
  body.append(ul(
    'Drag letters from your rack onto the grid, or tap a square then tap letters. Under a letter they go down, after one across; tap the same square again, or the arrow button, to switch.',
    'Tap a letter on the grid to build through it: the cursor jumps just past it, the way it is free. Tap it again for the other way.',
    'Drop a letter right on top of another and the two swap places — on the grid, in the rack, or one of each. Drop it just beside a letter and it goes in the free square there.',
    'Drag a letter within the rack to put it somewhere else in it.',
    'Undo and redo put your letters back as they were (Ctrl/Cmd + Z, Shift to redo).',
    'Type to place letters; Space switches between across and down; Backspace lifts.',
    'Hold a letter to pick it out; hold more to add them. Alt-hold picks out its whole word.',
    'Hold empty board for a second, then draw round the letters you want — any shape. Or use the loop tool to draw straight away.',
    'Drag any selected tile to move the whole group, or Ctrl/Cmd + arrows to nudge it.',
    'Drag a tile off the bottom of the board to send it back to your rack.',
    'Two fingers, or Ctrl + scroll wheel, zooms. If the host allows it, hold a tile in your rack to trade it in.',
    'Enter makes the call, / checks your words, \\ shuffles the rack, 0 re-centres; 1 moves the board, 2 draws round letters, 3 draws a rectangle.',
  ));

  body.append(h('The name'));
  body.append(p(
    `${BRAND.name} is said \u201c${BRAND.pronunciation}\u201d. It is made from the ` +
    `sentence that describes the game: ${BRAND.expansion}.`));
  return body;
}

/** The written rules on their own; the how-to-play page is usually better. */
export function openRules(config?: MatchConfig): void {
  openModal({
    title: `How to play ${BRAND.name}`,
    body: rulesBody(config),
    actions: [{ label: 'Got it', kind: 'primary', onClick: () => true }],
  });
}


// ---------------------------------------------------------------------------
// Privacy and terms
// ---------------------------------------------------------------------------

const PLACEHOLDER =
  'Nobody has been named as responsible for this game yet, and there is no ' +
  'way to send a privacy request. Until there is, please treat it as a ' +
  'private test among people who know each other, not a published service.';

/**
 * The privacy notice.
 *
 * Structured to cover what UK GDPR Article 13 asks for — controller, purposes
 * and lawful basis, recipients, transfers, retention, rights, and the route to
 * a regulator — rather than only describing the code. Every factual claim is
 * still a fact about the code: if any of it stops being true, this text has to
 * change with it.
 */
export function openPrivacy(config?: MatchConfig): void {
  const body = el('div', { style: 'color:var(--text-1);line-height:1.6' });
  const h = (t: string): HTMLElement => el('h3', { text: t, style: head() });
  const p = (t: string): HTMLElement => el('p', { text: t, style: 'margin:6px 0' });
  const ul = (...items: string[]): HTMLElement =>
    el('ul', { style: 'margin:6px 0;padding-left:20px' },
      ...items.map((i) => el('li', { text: i, style: 'margin:3px 0' })));

  const named = contactConfigured();
  if (!named) {
    body.append(el('div', { class: 'chip chip--warn', style: 'margin-bottom:12px',
      text: 'Not configured for publication' }));
    body.append(p(PLACEHOLDER));
  }

  body.append(h('Who is responsible'));
  body.append(p(named
    ? `${BRAND.name} is published by ${CONTROLLER.name}, who decides what ` +
      'happens to the little data it holds.'
    : 'Nobody has been named yet — see above.'));

  body.append(h('What is collected, and why'));
  body.append(p(
    'As little as a game can work with. You are never asked for your real ' +
    'name: one is made up for you — Copper Wren, Slate Dormouse — and you can ' +
    'change it to anything you like. If you choose to type your own name, that ' +
    'is your decision, not a question this game asked you.'));
  body.append(ul(
    'The made-up name you were given, or whatever you changed it to, so the ' +
    'other people at your table can tell who is who.',
    'A random identifier kept in your browser, so refreshing the page returns ' +
    'you to your own seat rather than losing your game.',
    'The letters and words of your match while it is running.',
    'Your settings — theme, sound, hints — which stay on your device and are ' +
    'never sent anywhere.',
  ));
  body.append(p(
    'The lawful basis is legitimate interests: running a game you chose to ' +
    'join, on the least information that makes it work. There are no accounts, ' +
    'no passwords, no email addresses, no payments and no chat. There is no ' +
    'advertising, no analytics and no tracking of any kind, so nothing here is ' +
    'used to build a profile of you or to make any decision about you.'));

  body.append(h('Who else sees it'));
  body.append(ul(
    'The people you invited. A table is reachable only through its invite ' +
    'link, is listed nowhere, and cannot be found by searching.',
    'Cloudflare, which hosts the game and — like any web host — handles your ' +
    'IP address in order to serve it.',
    'If you write in through the contact form, Web3Forms relays your message ' +
    'to us. They see what you type and the email address you give, if you ' +
    'give one. Nothing else on this page goes through them.',
  ));
  if (!config || config.peek.showDefinition) {
    body.append(p(
      'If you spend a check, the game may look that word up at ' +
      'api.dictionaryapi.dev to show its meaning. The request contains the ' +
      'word and nothing about you. Whether a word is valid is always decided ' +
      'offline from a list built into the game, so this is optional: the host ' +
      'can switch it off and the game works without it.'));
  }

  body.append(h('Where it goes'));
  body.append(p(
    'Cloudflare runs on servers around the world, so a table may be handled ' +
    'outside the UK or the European Economic Area. Cloudflare publishes a data ' +
    'processing agreement with the transfer safeguards this relies on.'));

  body.append(h('How long it is kept'));
  body.append(p(
    'Not long, on purpose. A table and everything in it — names, letters, ' +
    'grids and the invite key — is deleted automatically two hours after the ' +
    'last activity, and a finished match goes within ten minutes of everybody ' +
    'leaving. There is no archive and no backup of old games: once a table has ' +
    'gone, it is gone. What is stored in your own browser stays until you ' +
    'clear it, and clearing it is entirely up to you.'));

  body.append(h('Your rights, and how to use them'));
  body.append(p(
    'You can ask for a copy of what is held about you, ask for it to be ' +
    'corrected or deleted, ask that its use be restricted, object to it being ' +
    'used, or ask for it in a portable form.'));
  body.append(p(
    'Two of those you can do yourself, straight away, without asking anybody:'));
  body.append(ul(
    'Leave the table. Everything on the server goes within two hours, and ' +
    'within ten minutes if the match had finished.',
    'Clear this site\u2019s data in your browser settings. That removes the ' +
    'random identifier and your settings, which is everything kept on your ' +
    'own device.',
  ));
  if (named) {
    body.append(p(
      `For anything else, write to ${CONTROLLER.name}. You do not need an ` +
      'account, and you only need to give an email address if you want a reply.'));
    const write = el('button', {
      class: 'btn btn--sm', type: 'button', text: 'Make a privacy request',
    });
    on(write, 'click', () => openContact('Privacy request'));
    body.append(write);
  } else {
    body.append(p(
      'For anything else there should be a way to reach somebody here, and ' +
      'there is not one yet — which is part of why this game is not ready to ' +
      'be published. Please do not treat it as a live service.'));
  }
  body.append(p(
    'If you think this has been handled badly you can complain to a data ' +
    'protection regulator — in the UK the Information Commissioner\u2019s Office ' +
    '(ico.org.uk), or elsewhere your own country\u2019s authority. You can do ' +
    'that without contacting us first.'));

  body.append(h('Children'));
  body.append(p(
    'The game is built so a child can play alongside an adult, sharing one ' +
    'seat. It is not aimed at children playing alone. There is no chat, ' +
    'nothing to buy, no advertising and no way for a stranger to find a table. ' +
    'An adult should be the one who creates a table and chooses who is invited.'));

  body.append(h('Using the game'));
  body.append(ul(
    'Play nicely. The name you choose is visible to everyone at your table.',
    'The game is provided as is, with no warranty.',
    'The rules of a match are set by whoever created the table.',
  ));

  if (named) {
    body.append(h('Get in touch'));
    body.append(p('A question, a bug, an idea, or anything else about the game.'));
    const talk = el('button', { class: 'btn btn--sm', type: 'button', text: 'Get in touch' });
    on(talk, 'click', () => openContact('Feedback'));
    body.append(talk);
  }

  body.append(el('p', {
    style: 'margin:14px 0 0;font-size:12px;color:var(--text-2)',
    text: `© 2026 ${CONTROLLER.name}. Last updated ${CONTROLLER.updated}.`,
  }));

  openModal({
    title: 'Privacy and terms',
    body,
    actions: [{ label: 'Close', kind: 'primary', onClick: () => true }],
  });
}

/**
 * One line of the rules, with the values somebody chose picked out in yellow.
 * Given `onPick`, each value is a button that opens the options at that
 * setting — the value says what the rule is, and a tap is how it changes.
 */
export function ruleLine(line: RuleLine, onPick?: (setting: string) => void): HTMLElement {
  const para = el('p', { class: 'rules__line' });
  for (const part of line.parts) {
    if (typeof part === 'string') { para.append(document.createTextNode(part)); continue; }
    if (onPick && !NOT_A_SETTING.has(part.setting)) {
      const b = el('button', {
        class: 'rules__value rules__value--tap', type: 'button',
        title: `Change: ${part.setting}`, text: part.value,
      });
      on(b, 'click', () => onPick(part.setting));
      para.append(b);
    } else {
      para.append(el('b', { class: 'rules__value', title: `Setting: ${part.setting}`, text: part.value }));
    }
  }
  return para;
}

/** Values in the rules that are facts of the game, not settings anybody can change. */
const NOT_A_SETTING = new Set(['Always available']);
